import {
    Button,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    FormGroup,
    Input,
    Alert
} from "reactstrap";
import Select from 'react-select';
import { withNamespaces } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "axios";
import { 
    deleteUser,
    fetchAnalystAndAdminAnalysts,
    deleteAndReplaceUser
} from "../../api/apiFunctions";
import { connect } from "react-redux";
import { useEffect, useState } from "react";

const DeletionUserModal = function ({
    t,
    token,
    isOpen,
    onComplete,
    userIdToDelete,
    close,
    modules: {
        active: activeModule
    }
}) {
    const [errorHasOpenTasks, setErrorHasOpenTasks] = useState(false);
    const [showReassignOption, setShowReassignOption] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const {
        data: owners = [],
        isFetching: isOwnersLoading
    } = useQuery({
        queryKey: [
            "user-management-fetch-tasks-owners-list",
            activeModule
        ],
        queryFn: async () => {
            const response = await fetchAnalystAndAdminAnalysts({
                token: token,
                activeModule
            });

            return (response.data?.data || []).filter((owner) => owner.id !== userIdToDelete)
                .map((owner) => ({
                value: owner.id,
                label: `${owner.first_name} ${owner.last_name}`
            }));
        },
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("Unable to load the user list. Please try again."), {
                type: "error"
            });
        }
    });

    const handleCheckboxChange = (e) => {
        setShowReassignOption(e.target.checked);
        if (!e.target.checked) {
            setSelectedUserId(null);
        }
    };

    const deleteUserMutation = useMutation({
        mutationFn: async () => {
            await deleteUser({
                activeModule,
                token: token,
                userId: userIdToDelete
            });
        },
        onSuccess: () => {
            toast(t("User deleted successfully"), {
                type: "success"
            });

            onComplete();
        },
        onError: (error) => {
            if (axios.isAxiosError(error)) {
                if (error.response?.data?.error === "user_has_uncompleted_risk_task_report") {
                    setErrorHasOpenTasks(true);
                } 
                else if (error.response?.data?.error === "only_analysts_or_Admins_are_allowed_to_be_deleted") {
                    toast(t("Only analysts or admins can be deleted."), { type: "error" });
                }  else if (error.response?.data?.error === "user_is_tom_assigned_analyst") {
                    toast.error(
                        t("Cannot delete user. They are assigned as a TOM analyst. Please reassign or remove them from TOMs first.")
                      );
                } 
                else if (error.response) {
                    const data = error.response.data;
                    if (
                        data.error === "unknown_error" &&
                        data.message === "Access Denied."
                    ) {
                        toast(t("User cannot be deleted"), { type: "error" });
                    } else {
                        toast(t(data.message), { type: "error" });
                    }
                }
            } else {
                toast("An error occurred while deleting the user.", { type: "error" });
            }
        }
    });

    const deleteAndReplaceUserMutation = useMutation({
        mutationFn: async (payload) => {
            await deleteAndReplaceUser(payload);
        },
        onSuccess: () => {
            toast(t("User deleted. Active items reassigned."), {
                type: "success"
            });

            onComplete();
        },
        onError: () => {
            toast("An error occurred while deleting the user.", { type: "error" });
        }
    });

    const handleDelete = () => {
        if (showReassignOption){
            if(!selectedUserId) {
                toast(t("Please select a user to reassign active items."), { type: "error" });
                return;
            }

            deleteAndReplaceUserMutation.mutate({
                token,
                payload: {
                    fromUser: userIdToDelete,   
                    toUser: selectedUserId
                },
                activeModule
            });
            return;
        }

        deleteUserMutation.mutate()
    };

    useEffect(() => {
        if (!isOpen) {
            setErrorHasOpenTasks(false);
            setShowReassignOption(false);
            setSelectedUserId('');
        }
    }, [isOpen]);

    return (
        <>
            <Modal isOpen={isOpen} backdrop="static">
                <ModalHeader 
                    toggle={close}
                >
                    {t("Confirm Delete User")}
                </ModalHeader>

                <ModalBody>
                    {errorHasOpenTasks && (
                        <Alert color="danger">
                            {t('This user has active assignments and cannot be deleted directly.')}
                        </Alert>
                    )}

                    {t('Are you sure you want to delete user?')}

                    {errorHasOpenTasks && (
                        <FormGroup check className="mt-3">
                            <Label check>
                                <Input
                                    type="checkbox"
                                    checked={showReassignOption}
                                    onChange={handleCheckboxChange}
                                />{' '}
                                {t('Reassign active items to another user before deleting.')}
                            </Label>
                        </FormGroup>
                    )}

                    {showReassignOption && (
                        <FormGroup className="mt-3">
                            <Label for="reassignUserSelect">
                                {t('Select user to reassign active items')}
                            </Label>
                            <Select
                                required
                                name='owner'
                                classNamePrefix='select2-selection'
                                options={owners}
                                isDisabled={isOwnersLoading}
                                isLoading={isOwnersLoading}
                                onChange={(option) => setSelectedUserId(option.value)}
                                placeholder={t('Select...')}
                            />
                        </FormGroup>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        className="waves-effect waves-light"
                        type="button"
                        onClick={close}
                    >
                        {t("Cancel")}
                    </Button>

                    <Button
                        color="danger"
                        type="button"
                        disabled={
                            (errorHasOpenTasks && !selectedUserId) || 
                            deleteUserMutation.isLoading || 
                            deleteAndReplaceUserMutation.isLoading
                        }
                        onClick={handleDelete}
                    >
                        {(deleteUserMutation.isLoading || deleteAndReplaceUserMutation.isLoading) && (
                            <Spinner
                                className="me-2"
                                size="sm"
                                color="white"
                            />
                        )}

                        {t("Delete User")}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

const mapStatetoProps = (state) => {
	const { token } = state.Login;
	const { Modules } = state;

	return {
		token,
		modules: Modules
	};
};

export default withNamespaces()(
	connect(mapStatetoProps)(
        DeletionUserModal
    )
);
